import React from "react";
import { Box, Chip, CircularProgress, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import { DataRow, Panel } from "scenes/server/serverUi";
import { useServerData } from "scenes/server/ServerDataProvider";

/**
 * Monitoring setup (2026-10-21).
 *
 * Answers "is monitoring actually working?" — the most common silent failure
 * is a collector that was never installed, which otherwise looks identical to
 * a perfectly healthy server.
 */

// Thin wrappers over the shared primitives so the Setup tab keeps its
// key/value shape but shares one card style with the rest of the screen.
const Row = ({ label, value, state }) => (
  <DataRow
    label={label}
    value={value}
    tone={
      state === "ok" ? "ok" : state === "warn" ? "warning" : state === "error" ? "critical" : undefined
    }
  />
);

const Section = ({ title, children }) => <Panel title={title}>{children}</Panel>;

const ServerSetup = () => {
  const theme = useTheme();

  // Shared with the header and the Stats tab — this tab used to fetch the same
  // two payloads again on mount.
  const { snapshot, config, staleness, loading } = useServerData();

  if (loading && !snapshot) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const collectorRunning = Boolean(snapshot) && !staleness.isStale;

  const services = Array.isArray(snapshot?.Services) ? snapshot.Services : [];
  const probeFailures = services.filter((service) => service.status === "unknown");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Panel
        icon={
          collectorRunning ? <CheckCircleRoundedIcon /> : <ErrorRoundedIcon />
        }
        accent={collectorRunning ? "ok" : "critical"}
        title={collectorRunning ? "Collector is running" : "Collector is NOT reporting"}
        subtitle={
          collectorRunning
            ? `Last snapshot ${staleness.ageSeconds}s ago. Expected every 60s.`
            : snapshot
              ? `Last snapshot was ${Math.round((staleness.ageSeconds || 0) / 60)} minute(s) ago — check the system cron job.`
              : "No snapshot has ever been collected. Install the cron job (see below)."
        }
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Section title="Collector">
            <Row
              label="Schedule"
              value="System cron, every minute — survives app restarts"
              state="ok"
            />
            <Row
              label="Stale threshold"
              value={`${config?.staleAfterSeconds || 180}s (3 missed runs)`}
              state="ok"
            />
            <Row
              label="Retention"
              value={`${config?.metricsRetentionDays || 14} days of raw snapshots`}
              state="ok"
            />
            <Row
              label="Last collection"
              value={
                snapshot?.CapturedAt
                  ? `${new Date(snapshot.CapturedAt).toLocaleString()} (${snapshot.CollectedBy || "cron"})`
                  : "never"
              }
              state={collectorRunning ? "ok" : "error"}
            />
          </Section>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Section title="Monitored paths">
            <Row
              label="Disk mount"
              value={config?.diskMount || "/"}
              state={snapshot?.DiskPercent !== null && snapshot?.DiskPercent !== undefined ? "ok" : "warn"}
            />
            <Row
              label="Backup directory"
              value={config?.backupDirectory || "/db_backups"}
              state={
                snapshot?.Backups?.status === "ok"
                  ? "ok"
                  : snapshot?.Backups?.status === "unavailable"
                    ? "error"
                    : "warn"
              }
            />
            <Row
              label="SSL certificates"
              value={
                config?.sslConfigured
                  ? (config.sslDomains || []).join(", ") || "configured"
                  : "Not configured — set SERVER_SSL_CERTS to monitor expiry"
              }
              state={config?.sslConfigured ? "ok" : "warn"}
            />
            <Row
              label="Offsite storage"
              value={
                config?.rcloneConfigured
                  ? config.rcloneRemote
                  : "Not configured — set SERVER_RCLONE_REMOTE to watch the Drive quota"
              }
              state={
                !config?.rcloneConfigured
                  ? "warn"
                  : snapshot?.Backups?.remote?.status === "ok" ||
                      snapshot?.Backups?.remote?.status === "unlimited"
                    ? "ok"
                    : "error"
              }
            />
          </Section>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Section title="Service probes">
            {services.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No service probes in the latest snapshot.
              </Typography>
            )}
            {services.map((service) => (
              <Row
                key={service.name}
                label={`${service.name} — ${service.status}`}
                value={service.detail}
                state={service.status === "running" ? "ok" : "warn"}
              />
            ))}

            {probeFailures.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  label={`${probeFailures.length} probe(s) could not resolve`}
                  sx={{
                    height: 22,
                    borderRadius: "6px",
                    fontSize: "0.7rem",
                    bgcolor: `${theme.palette.warning.main}1a`,
                    color: theme.palette.warning.main,
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.75 }}
                >
                  “Unknown” means neither a systemd unit nor a listening port was
                  found. That is usually a wrong unit name — not an outage.
                </Typography>
              </Box>
            )}
          </Section>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Section title="Install the collector on the VPS">
            <Typography
              variant="body2"
              component="pre"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                m: 0,
                p: 1.5,
                borderRadius: "8px",
                bgcolor: theme.palette.action.hover,
              }}
            >
              {`# 1. Migration
cd /var/www/ce_portal_server && npx sequelize db:migrate

# 2. Cron (as the app user, not root)
crontab -e
* * * * * cd /var/www/ce_portal_server && /usr/bin/node scripts/collect_server_metrics.js >> logs/metrics-cron.log 2>&1

# 3. Verify
tail -f /var/www/ce_portal_server/logs/metrics-cron.log`}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Full guide with sudoers notes:{" "}
              <code>server/docs/server-monitoring-setup.md</code>
            </Typography>
          </Section>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ServerSetup;
